// src/contexts/AccountContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext"; // Assuming you have an AuthContext to get the current user
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase"; // Assuming you have initialized Firestore in a firebase.ts file
import { Account, Integration } from "@visitproof/types";
import { AccountService } from "../services/AccountService";

// Define the AccountContext type
interface AccountContextType {
  account: Account;
  isAdmin: boolean;
  isInquiryEnabled: boolean;
  isChatEnabled: boolean;
  isFL324PExportEnabled: boolean;
  loading: boolean;
  error: string | null;

  integrations: Integration[];
  deleteIntegration: (id: string) => Promise<void>;
}

// Create the context
const AccountContext = createContext<AccountContextType | undefined>(undefined);

// Create a provider component
export const AccountProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [account, setAccount] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Configurations
  const [isInquiryEnabled, setIsInquiryEnabled] = useState<boolean>(false);
  const [isChatEnabled, setIsChatEnabled] = useState<boolean>(false);
  const [isFL324PExportEnabled, setIsFL324PExportEnabled] =
    useState<boolean>(false);
  const [integrations, setIntegrations] = useState<Integration[]>([]);

  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    // If auth is still loading, wait for it
    if (authLoading) return;

    // If no user after auth finished loading, set loading to false
    if (!user) {
      setLoading(false);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const loadAccount = async () => {
      try {
        // First, get the user's claims to check their role
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult.claims;
        const role = claims.role as string | undefined;
        const accountIds = claims.accountIds as string[] | undefined;

        // Check if user has a selected account in localStorage
        const selectedAccountId = localStorage.getItem('visitproof_selected_account');

        // Use selected account if it's valid (exists in user's accountIds), otherwise use first
        const accountId = (selectedAccountId && accountIds?.includes(selectedAccountId))
          ? selectedAccountId
          : accountIds?.[0];

        // Determine if this user is an admin/staff member for the selected account
        // Check the account-specific role from claims.accounts, fallback to global role
        const accountClaims = claims.accounts as Record<string, { role?: string; roles?: string[] }> | undefined;
        const accountRole = accountClaims?.[accountId || '']?.role;
        const accountRoles = accountClaims?.[accountId || '']?.roles || [];
        const isAdminRole = accountRole === 'admin' || accountRole === 'owner' ||
          accountRoles.includes('admin') || accountRoles.includes('owner') ||
          role === 'admin' || role === 'owner';

        // If user has an accountId in claims, use it directly
        // This handles both invited staff and supervisors/parents
        if (accountId) {
          const accountDocRef = doc(db, "accounts", accountId);
          unsubscribe = onSnapshot(
            accountDocRef,
            (accountDoc) => {
              if (accountDoc.exists()) {
                const accountData = accountDoc.data();
                setAccount({ id: accountDoc.id, ...accountData } as Account);
                // User is admin if they have admin role or if they're the account owner
                setIsAdmin(isAdminRole || accountData.adminId === user.uid);
                setIsInquiryEnabled(
                  accountData?.configuration?.isInquiryEnabled ?? false,
                );
                setIsChatEnabled(
                  accountData?.configuration?.isChatEnabled ?? false,
                );
                setIsFL324PExportEnabled(
                  accountData?.configuration?.isFL324PExportEnabled ?? false,
                );
              }
              setLoading(false);
            },
            (err) => {
              console.error("Error fetching account: ", err);
              // Don't show error - just finish loading
              // User may not have permission to read the account document
              setLoading(false);
            }
          );
          return;
        }

        // Fallback for users without accountId in claims:
        // Try to find account where user is the owner (adminId)
        const adminQuery = query(
          collection(db, "accounts"),
          where("adminId", "==", user.uid),
        );

        unsubscribe = onSnapshot(
          adminQuery,
          async (docSnapshot) => {
            if (!docSnapshot.empty) {
              // User is the account owner
              const accountDoc = docSnapshot.docs[0];
              setAccount({ id: accountDoc.id, ...accountDoc.data() } as Account);
              setIsAdmin(true);
              setIsInquiryEnabled(
                accountDoc.data().configuration?.isInquiryEnabled ?? false,
              );
              setIsChatEnabled(
                accountDoc.data().configuration?.isChatEnabled ?? false,
              );
              setIsFL324PExportEnabled(
                accountDoc.data().configuration?.isFL324PExportEnabled ?? false,
              );
              setLoading(false);
            } else {
              // No account found - might be a new user or different role
              setLoading(false);
            }
          },
          (err) => {
            console.error("Error fetching account: ", err);
            setError(`Failed to listen to account changes: ${err.message}`);
            setLoading(false);
          },
        );
      } catch (err) {
        console.error("Error loading account:", err);
        setError(err instanceof Error ? err.message : "Failed to load account");
        setLoading(false);
      }
    };

    loadAccount();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, authLoading]);

  useEffect(() => {
    if (!account) return;
    const docRef = query(
      collection(db, "accounts", account.id, "integrations"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setIntegrations(
          docSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Integration[],
        );
      },
      (err) => {
        console.error("Error fetching integrations: ", err);
        setError(`Failed to listen to integration changes: ${err.message}`);
      },
    );
    return () => unsubscribe();
  }, [account]);

  const deleteIntegration = async (id: string) => {
    try {
      await AccountService.deleteAccountIntegration(account.id, id);
    } catch (error) {
      console.error(`Error deleting integration:`, error);
      throw new Error(`Failed to delete integration: ${error}`);
    }
  };

  return (
    <AccountContext.Provider
      value={{
        account,
        isAdmin,
        isInquiryEnabled,
        isChatEnabled,
        isFL324PExportEnabled,
        integrations,
        deleteIntegration,
        loading,
        error,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

// Custom hook for using the AccountContext
export const useAccount = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error("useAccount must be used within an AccountProvider");
  }
  return context;
};

// Optional hook that returns null if not within AccountProvider (for components that may render outside the provider)
export const useAccountOptional = (): AccountContextType | null => {
  return useContext(AccountContext) ?? null;
};
